'use client';
import React, { useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { useAuth } from '@/contexts/AuthContext';
import AppLogo from '@/components/ui/AppLogo';
import { Eye, EyeOff, Loader2, Building2, TrendingUp, BarChart3, Shield, CheckCircle2 } from 'lucide-react';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface SignupFormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

export default function AuthContent() {
  const { signIn, signUp } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const loginForm = useForm<LoginFormData>({ defaultValues: { rememberMe: false } });
  const signupForm = useForm<SignupFormData>();

  // Show success message if redirected from signup
  useEffect(() => {
    if (searchParams?.get('signup') === 'success') {
      setSuccessMessage('Account created successfully. Please sign in to continue.');
      setMode('login');
    }
  }, [searchParams]);

  const handleLogin = async (data: LoginFormData) => {
    setIsLoading(true);
    setAuthError('');
    setSuccessMessage('');
    try {
      await signIn(data.email, data.password);
      router.push('/');
      router.refresh();
    } catch (err: any) {
      setAuthError(err?.message || 'Invalid email or password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (data: SignupFormData) => {
    if (data.password !== data.confirmPassword) {
      signupForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setIsLoading(true);
    setAuthError('');
    try {
      // Role is always 'user' — never passed from frontend
      await signUp(data.email, data.password, {
        fullName: data.name,
      });
      // After signup, user is signed out inside signUp(). Redirect to login with success message.
      router.push('/sign-up-login?signup=success');
    } catch (err: any) {
      setAuthError(err?.message || 'Sign up failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const inputClass = (hasError?: boolean) =>
    `w-full px-3 py-2.5 text-sm bg-muted border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-foreground placeholder:text-muted-foreground transition-colors ${
      hasError ? 'border-red-400 focus:ring-red-200 focus:border-red-400' : 'border-input'
    }`;

  const features = [
    { icon: <Building2 size={18} />, text: 'Track unlimited investment properties' },
    { icon: <TrendingUp size={18} />, text: 'Analyze ROI, cap rate, and cash flow' },
    { icon: <BarChart3 size={18} />, text: 'Visual pipeline dashboard' },
    { icon: <Shield size={18} />, text: 'Private workspace for each user' },
  ];

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-[480px] xl:w-[560px] flex-col bg-primary p-10 xl:p-14 relative overflow-hidden flex-shrink-0">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-[-80px] right-[-80px] w-[400px] h-[400px] rounded-full bg-white/5" />
          <div className="absolute bottom-[-60px] left-[-60px] w-[300px] h-[300px] rounded-full bg-white/5" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-white/3" />
        </div>

        <div className="relative z-10 flex flex-col h-full">
          <div className="flex items-center gap-3">
            <AppLogo size={40} />
            <span className="text-white font-bold text-xl tracking-tight">PropertyTracker</span>
          </div>

          <div className="mt-16 xl:mt-20">
            <h1 className="text-3xl xl:text-4xl font-bold text-white leading-tight">
              Your real estate investment pipeline, organized.
            </h1>
            <p className="text-white/70 text-base mt-4 leading-relaxed">
              Log properties, track inspections, analyze financials, and make confident investment decisions — all in one place.
            </p>
          </div>

          <div className="mt-10 space-y-4">
            {features.map((f, i) => (
              <div key={`feat-${i + 1}`} className="flex items-center gap-3">
                <div className="w-9 h-9 bg-white/15 rounded-lg flex items-center justify-center flex-shrink-0 text-white">
                  {f.icon}
                </div>
                <span className="text-white/85 text-sm">{f.text}</span>
              </div>
            ))}
          </div>

          <div className="mt-auto pt-10 grid grid-cols-3 gap-4">
            {[
              { value: 'Real', label: 'Live Data' },
              { value: 'Secure', label: 'Supabase Auth' },
              { value: 'Private', label: 'Your Data Only' },
            ].map(stat => (
              <div key={`brand-stat-${stat.label}`} className="text-center">
                <p className="text-2xl font-bold text-white font-mono-nums">{stat.value}</p>
                <p className="text-xs text-white/60 mt-0.5">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <AppLogo size={36} />
            <span className="font-bold text-lg text-foreground">PropertyTracker</span>
          </div>

          {/* Login / Signup tabs */}
          <div className="flex gap-1 bg-muted p-1 rounded-xl mb-8">
            <button
              onClick={() => { setMode('login'); setAuthError(''); }}
              className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all duration-150 ${
                mode === 'login' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => { setMode('signup'); setAuthError(''); setSuccessMessage(''); }}
              className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all duration-150 ${
                mode === 'signup' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Create Account
            </button>
          </div>

          {/* Success message (shown after signup redirect) */}
          {successMessage && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
              <CheckCircle2 size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-green-700">{successMessage}</p>
            </div>
          )}

          {authError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{authError}</p>
            </div>
          )}

          {/* Login Form */}
          {mode === 'login' && (
            <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Email address</label>
                <input
                  type="email"
                  autoComplete="email"
                  placeholder="your@email.com"
                  {...loginForm.register('email', {
                    required: 'Email is required',
                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                  })}
                  className={inputClass(!!loginForm.formState.errors.email)}
                />
                {loginForm.formState.errors.email && (
                  <p className="text-xs text-red-500 mt-1">{loginForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium text-foreground">Password</label>
                  <Link
                    href="/forgot-password"
                    className="text-xs text-primary hover:underline font-medium"
                  >
                    Forgot Password?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    {...loginForm.register('password', {
                      required: 'Password is required',
                      minLength: { value: 6, message: 'Minimum 6 characters' },
                    })}
                    className={`${inputClass(!!loginForm.formState.errors.password)} pr-10`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(prev => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                {loginForm.formState.errors.password && (
                  <p className="text-xs text-red-500 mt-1">{loginForm.formState.errors.password.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground text-sm font-semibold py-3 rounded-lg hover:bg-primary/90 disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-150 active:scale-[0.99]"
              >
                {isLoading ? (
                  <><Loader2 size={16} className="animate-spin" /> Signing in...</>
                ) : (
                  'Sign In'
                )}
              </button>

              <p className="text-center text-xs text-muted-foreground">
                Don&apos;t have an account?{' '}
                <button
                  type="button"
                  onClick={() => { setMode('signup'); setAuthError(''); setSuccessMessage(''); }}
                  className="text-primary font-medium hover:underline"
                >
                  Create one
                </button>
              </p>
            </form>
          )}

          {/* Signup Form — no role selector */}
          {mode === 'signup' && (
            <form onSubmit={signupForm.handleSubmit(handleSignup)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Full Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  placeholder="Your full name"
                  {...signupForm.register('name', { required: 'Name is required' })}
                  className={inputClass(!!signupForm.formState.errors.name)}
                />
                {signupForm.formState.errors.name && (
                  <p className="text-xs text-red-500 mt-1">{signupForm.formState.errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Email address <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  autoComplete="email"
                  placeholder="your@email.com"
                  {...signupForm.register('email', {
                    required: 'Email is required',
                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                  })}
                  className={inputClass(!!signupForm.formState.errors.email)}
                />
                {signupForm.formState.errors.email && (
                  <p className="text-xs text-red-500 mt-1">{signupForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Password <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="Min. 8 characters"
                    {...signupForm.register('password', {
                      required: 'Password is required',
                      minLength: { value: 8, message: 'Minimum 8 characters' },
                    })}
                    className={`${inputClass(!!signupForm.formState.errors.password)} pr-10`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(prev => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label="Toggle password"
                  >
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                {signupForm.formState.errors.password && (
                  <p className="text-xs text-red-500 mt-1">{signupForm.formState.errors.password.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Confirm Password <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="Repeat password"
                    {...signupForm.register('confirmPassword', { required: 'Please confirm your password' })}
                    className={`${inputClass(!!signupForm.formState.errors.confirmPassword)} pr-10`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(prev => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label="Toggle confirm password"
                  >
                    {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                {signupForm.formState.errors.confirmPassword && (
                  <p className="text-xs text-red-500 mt-1">{signupForm.formState.errors.confirmPassword.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground text-sm font-semibold py-3 rounded-lg hover:bg-primary/90 disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-150 active:scale-[0.99]"
              >
                {isLoading ? (
                  <><Loader2 size={16} className="animate-spin" /> Creating account...</>
                ) : (
                  'Create Account'
                )}
              </button>

              <p className="text-center text-xs text-muted-foreground">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => { setMode('login'); setAuthError(''); setSuccessMessage(''); }}
                  className="text-primary font-medium hover:underline"
                >
                  Sign in
                </button>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}